// A switch's ports, managed from its page (#293, ZENITH.md §11.3): the intent
// the site holds for each one, an editor per port, the plan that shows the
// exact document an apply would send and the uplink the rule protected, and
// the apply of that plan's digest and nothing else.
//
// **Managed from Design, read from Operate** (#875, ADR-0149 decision 1). The
// editor, the plan, the apply and the port templates are Design › Wired's
// Ports tab (`WiredPorts.tsx`), which hands this component its writes
// (`PortWrites`, from `portWrites.ts`) and its templates. A device's page is
// Operate's and hands neither, so the same table and detail render there with
// no control that writes — the plan's comparison (`Compared`) is Design's
// alone since #1801 — and this module holds no write
// for Operate to reach — `shell/operate.test.ts` walks Operate's imports and
// says so if it ever does.
//
// Three paths and no others, all of them `/v1`'s since #94 and #271:
// `GET`/`PUT /switches/{serial}/ports` store and answer intent and touch no
// controller; `:plan` reads the switch's own configuration, merges the intent
// into it and proves the result; `:apply` recomputes the plan, compares the
// digest and sends. **The plan and the apply are one act** (#1891): the
// pending bar's *Review & apply* opens the sheet (`ReviewApply.tsx`) that
// reads the switch, plans, asks the one confirmation and applies, and its
// apply button carries the plan on screen's digest (§3 principle 9): there is
// no way from the editor to a controller write.
//
// A port selected on the faceplate opens its detail (#294, `PortDetail.tsx`):
// what the cell holds for it beside the intent, the plan or a past apply,
// with the fields an apply would change marked from the plan's own `changes`;
// and the faceplate marks every port the plan would change. The detail also
// says what the port is doing now and offers its intent acts (#367): the
// editor then opens beside it rather than under the table.
//
// The faceplate is drawn from the catalogue model the device resolved to when
// there is one — every port, the authored layout, the photo (#310, #298) —
// from the ports the switch's own configuration declares when the live read
// answers, and from the intent alone otherwise; `DeviceDetail` reads the model.
// **It is drawn at the head of the page and not here** (#589): `portsOf` is
// what this file still owns, `DeviceDetail` computes the face from it once
// and hands it to the head (`DeviceFaceHead`) and to this tab, so the ports
// pressed on the picture are the page's set — this tab's rows, its detail and
// its editors follow it, and the plan a press here asks for is the page's
// too, so the change layer it paints is on the face above whichever tab is
// open.
//
// **The live layer** (#295, §11.3, ADR-0045 decision 3) is one more read,
// `GET /switches/{serial}/ports/state`: what each port is doing right now —
// link, speed, power, and the LLDP neighbour — read from the gateway when the
// page opens and again when the operator asks, stored nowhere and never
// polled. The read is the page's (`useLiveState` in `DeviceDetail`, #371),
// made once and handed here and to the neighbourhood, so the two draw from
// one answer. It is painted on the faceplate, shown in two columns of the
// table and in the selected port's detail (#367), and a neighbour that is a device of the site is a link to it; one
// that is not is named by what the switch reports of it — its system name,
// else its management address, else its MAC (#1480) — and nothing more. A field the switch did not answer says why, in the
// API's own reasons; a port the report spells in a vocabulary this build does
// not recognise is `unanswered`, never translated (decision 3). A port's link
// is not the device's online state: the identity panel says whether the
// switch is up, and this says what its ports are doing (§9.4).
//
// **A switch whose cell holds no configuration document** (#1429, ADR-0266)
// is answered `keyed_by: report`: its unit PoE, and its ports as its own
// report names them, each unresolved against any document. Those are listed
// at the end of the one ports table, under a heading of their own
// (`ReportKeyedNotice` says what they are, once, above it; #1801), and are
// not the face's rows: no faceplate, intent, link or console reading is
// matched to a report key, here or anywhere (`keyedBy.ts`).
//
// **The cabled layer** (#297) is the declared links that end at this switch,
// painted on the ports they name from `cabled` — intent, read from
// `/sites/{id}/links` by the page that mounts this, listed on a device's
// Links tab (#388) and declared or withdrawn on Design › Wired's topology.
//
// **Several ports at once** (#473). The faceplate always gathers a set —
// a click, a rectangle, Shift and Ctrl/⌘ (#467) — and the table's name
// buttons press the same set with the same modifiers. Exactly one port in
// the set is the selected port: its detail opens and its own editor is
// offered, as before. Any number of them is what the bulk editor
// (`PortsEditor.tsx`) describes in one act: each field unchanged
// unless set, a preview per port, and one whole-set PUT — the same write the
// single editor makes, with the same generations and the same stale refusal.
// The set is the page's (#589), so a port picked here is the port pressed on
// the Links tab.
//
// **A port with no intent is described from what the cell holds for it**
// (#571, ADR-0078 decision 5). The single editor used to open blank, and a
// blank form saved is the port's whole intent: the next apply replaced the
// port's VLANs and PoE with it, which is the omission hazard by another door.
// `/v1` exposes no read of a switch's document outside a plan, so the held
// fields are a plan's `changes[].held` — the plan on screen when there is
// one, and otherwise a `:plan` made when the editor opens, which sends
// nothing and is not put on screen as something to apply. The editor says
// the values are **held** by the cell and when they were read; where they
// could not be read it says that, and that the blank form is not the port.
// The bulk editor composes a first intent from the same read.
//
// **VLAN names on this page** (ADR-0064, #590). The page reads the site's
// tag space once, `/sites/{id}/vlans` (`useTagSpace`, in `DeviceDetail`
// since the face moved to the head), and hands it to the bulk editor's
// chooser; the table's VLANs column and the line under the face at the head
// read a tagged set by the same names — *Rooms (1001)* for a group the port
// carries whole, numbers and runs for the rest — with the numbers one hover
// away, and the port detail's comparison reads both of its sides the same
// way. The untagged VLAN, the single editor's text field and the plan's text
// still show numbers, each a follow-up; a name is a label and enters nothing
// that is sent.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation } from "../../api/mutation";
import type { Problem } from "../../api/problem";
import type { components } from "../../api/schema";
import { plural, reportAge } from "../../dashboard/format";
import { scopeNames, useEstate } from "../../shell/estate";
import { Info, Pill, ProblemBox, SpecForm, num, str, text, type FormValues } from "../../ui";
import type { Device } from "./commands";
import { PortName, applySelection, byNumber, modeOf, portKindOf, type FacePhoto, type FacePort, type PortState } from "./DeviceFace";
import { PortVlanFlag, vlansByPort } from "./switchVlans";
import { modelImageURL, type DeviceModel, type useLiveState } from "./DeviceDetail";
import type { Face } from "./DeviceHead";
import Compared from "./Compared";
import PortDetail, { PortActions } from "./PortDetail";
import PortsEditor, { EMPTY_EDIT, composeSet, entryOf, isEmptyEdit, type BulkEdit, type TagSpace } from "./PortsEditor";
import { Inspector, PendingBar, type Pending } from "./PortsInspector";
import { unpinnedFirmwareSentence } from "./SwitchPlanFacts";
import SwitchSettings, { settingsKey } from "./SwitchSettings";
import SwitchCliSettings, { type ApplyCliSetting, type ReadCliAdmission } from "./SwitchCliSettings";
import { PoECell, PoERead, type PoEReading } from "./PortPoE";
import { documentKeyed, documentKeyedRead } from "./keyedBy";
import { runsWords, setWords, type Space } from "../networks/vlanModel";
import { unrecognized } from "../../api/openEnum";
import { DeviceRef } from "../../shell/EntityRef";

export type PortIntent = components["schemas"]["PortIntent"];
export type PortSet = components["schemas"]["PortIntentSet"];
export type LiveSet = components["schemas"]["SwitchPortStateSet"];

/** When the report on screen was written and when it was read (#466). The
 * gateway hands back the report the switch last pushed, on the switch's own
 * schedule, so a read made now can carry a report minutes old; `unchanged`
 * is a read that brought the same report the previous one did. */
export type ReportTimes = { readAt?: Date; reportedAt?: Date; unchanged: boolean };

export function useReportTimes(set?: LiveSet): ReportTimes {
  // When the answer on screen arrived: a new Date for each new answer.
  const readAt = useMemo(() => (set ? new Date() : undefined), [set]);
  // The answer before this one, kept to tell whether reading again brought
  // anything newer: updated while rendering a new answer, React's form for
  // state that follows an input, and a no-op for an answer already seen.
  const [seen, setSeen] = useState<{ set?: LiveSet; previous?: string }>({});
  if (set && seen.set !== set) setSeen({ set, previous: seen.set?.reported_at });
  const reported = set?.reported_at;
  return {
    readAt,
    reportedAt: reported ? new Date(reported) : undefined,
    unchanged: !!reported && seen.set === set && seen.previous === reported,
  };
}

// `reportAge` moved to ../../dashboard/format (#1165): Operate › Wireless says
// the same sentence about an access point's report, and a sentence two pages
// say belongs with the formatting rather than in one of them. This comment
// said importing it from here would pull this whole page into that chunk, and
// it would not have: this module is eager through the device page, so a lazy
// page importing it downloads nothing more (#1173). Re-exported so every
// existing importer — and the test that pins the wording — keeps its path.
export { reportAge } from "../../dashboard/format";

/** The sentence both views say about one answer: when the switch wrote the
 * report, when it was read, and whether reading again brought a newer one. */
export function ReportWhen({ times }: { times: ReportTimes }) {
  const { readAt, reportedAt, unchanged } = times;
  return (
    <>
      {reportedAt && readAt
        ? <>Reported by the switch at {reportedAt.toLocaleTimeString()} ({reportAge(reportedAt, readAt)})</>
        : <>The report does not say when the switch wrote it</>}
      {readAt && <>; read from the controller at {readAt.toLocaleTimeString()}</>}, stored nowhere.
      {unchanged && <> No newer report has arrived since the last read. The switch sends reports on its own schedule, so a link that changed since then shows in its next report.</>}
    </>
  );
}
export type LivePort = components["schemas"]["SwitchPortState"];
type LiveNeighbour = components["schemas"]["SwitchPortNeighbour"];
type Entry = components["schemas"]["PortIntentEntry"];

/** The three writes a switch's ports take (§11.3): store the whole intent
 * set, plan it, and apply the plan's digest. **Handed in, never imported
 * here** (#875): Design › Wired's Ports tab passes `PORT_WRITES`
 * (`portWrites.ts`) for a caller who holds `network.write` at the site, and a
 * device's page in Operate passes nothing. `plan` sends nothing, but it is a
 * POST, it reads the switch's console on a measured pair (ADR-0321), and it
 * belongs with the act that asks for it: *Review & apply*, never an editor
 * opening (#2387). `readHeld` is what an editor asks instead. */
export interface PortWrites {
  /** `extra` carries the set's own fields beside its ports — a switch's voice
   * VLAN (ADR-0369 decision 1a) — and is absent on every port edit. */
  put(serial: string, ports: Entry[], extra?: Omit<components["schemas"]["PortIntentSetRequest"], "ports">): Promise<{ error?: unknown; response: Response }>;
  /** Each port as the document the cell holds says it (#2387): no plan and no console session. */
  readHeld(serial: string): Promise<{ data?: components["schemas"]["SwitchHeldPorts"]; error?: unknown; response: Response }>;
  plan(serial: string): Promise<{ data?: Plan; error?: unknown; response: Response }>;
  /** Answers `202` with the apply's id (ADR-0480, #2061); what the switch did is the apply's record. */
  apply(serial: string, digest: string, acceptedPortReach?: string[], acceptedRemovedFates?: string[], confirmWindowSeconds?: number, acceptedLoopFindings?: string[]): Promise<{ data?: components["schemas"]["ApplyAccepted"]; error?: unknown; response: Response }>;
  /** A setting the schema has no key for, by the switch's command line (ADR-0417, #1850): not part of
   * the plan and apply, and drawn in its own section. */
  applyCliSetting?: ApplyCliSetting;
  /** Which of those settings the switch admits (#1868): a hint to disable and explain up front; the write's gate stays the gate, its refusal on the apply's record (ADR-0480, #2061). */
  readCliAdmission?: ReadCliAdmission;
}
export type Plan = components["schemas"]["SwitchPortPlan"];

/** What the page hands the *Review & apply* sheet (#1891). The sheet makes
 * writes, so it is **handed in as `review`, never imported here**, exactly as
 * `PortWrites` is (#875): Design › Wired's Ports tab renders `ReviewApply`
 * with these, and a device's page in Operate hands nothing. */
export interface ReviewProps {
  plan?: Plan;
  onPlan(plan: Plan | undefined): void;
  mayRead: boolean;
  whose: { known: boolean; customer?: string; site?: string };
  space?: Space;
  onClose(): void;
  onImported(): void;
  onApplied(): void;
  onPick(port: string): void;
  /** How many ports the site holds planned settings for on this switch: a stub-held switch with any is offered its
   * first configuration with them (ADR-0555 decision 7a). */
  intentPorts?: number;
}

/** The ports to draw and what is painted on each.
 *
 * With a catalogue model (#310): every port the model declares, in the
 * model's order, placed by its authored layout, with the intent, the uplink
 * and the plan's changes painted on the ports they name. A port intent or
 * the switch names and the model does not declare is still drawn, after the
 * model's — the absence rule at the component's scale — with a detail saying
 * so. Without a model: the ports the intent set names plus the uplink, and
 * every port the live read answers for, since those are the switch's own
 * document's, in the order the switch numbers them. With a plan on screen,
 * every port whose fields the apply would change carries the change layer —
 * from the plan's own `changes`, which the server computed from the two
 * documents; nothing here compares anything (#294). With a live answer
 * (#295), each port carries its link and whether it delivers power, and the
 * speed, the neighbour and any unanswered field in its detail.
 *
 * **The detail names which words are whose** (#1615). A port's words are two
 * planes, and they were one run under the live line's *as the switch last
 * reported it*: `access · access VLAN 3001 · … · 100 Mb/s`, which read Design's
 * intent as the switch's state. Now the intent's words follow `INTENT_LABEL`
 * and the report's follow `REPORT_LABEL`, the two segments joined by `; `, so
 * an intended VLAN never stands unlabelled beside what the switch said. */
// Restates ADR-0078 decision 3 and ADR-0149 — intent is Design's record and the
// report is the switch's, and neither is rendered as the other.
export function portsOf(set: PortSet, plan?: Plan, model?: DeviceModel, live?: LiveSet, cabled: Record<string, string> = {}, space?: Space): { ports: FacePort[]; states: Record<string, PortState> } {
  const states: Record<string, PortState> = {};
  const names = new Set<string>();
  for (const p of set.ports) {
    names.add(p.port);
    states[p.port] = { intent: p.enabled ? "described" : "disabled", detail: `${INTENT_LABEL} ${describe(p, space)}` };
  }
  for (const [port, to] of Object.entries(cabled)) {
    names.add(port);
    states[port] = { ...states[port], cabled: to };
  }
  for (const port of uplinkMembers(set)) {
    names.add(port);
    states[port] = { ...states[port], uplink: true };
  }
  for (const c of plan?.changes ?? []) {
    if (c.differs.length === 0) continue;
    names.add(c.port);
    states[c.port] = { ...states[c.port], change: true };
  }
  // A report-keyed answer paints nothing: its keys are not ports of the
  // intent, the model or any configuration, however they are spelt (ADR-0266).
  for (const p of (live ? documentKeyed(live) : undefined)?.ports ?? []) {
    names.add(p.port);
    const s: PortState = { ...states[p.port] };
    if (p.link === "up" || p.link === "down") s.link = p.link;
    if (p.poe_draw_mw !== undefined && p.poe_draw_mw > 0) s.poe = true;
    // A link state this build does not know paints nothing and is named in the words (ADR-0511 5a).
    const unlistedLink = p.link && p.link !== "up" && p.link !== "down" ? `link ${unrecognized(p.link)}` : undefined;
    const words = [liveWords(p), unlistedLink].filter(Boolean).join("; ");
    if (words) s.detail = s.detail ? `${s.detail}; ${REPORT_LABEL} ${words}` : `${REPORT_LABEL} ${words}`;
    states[p.port] = s;
  }
  if (!model || model.ports.length === 0) {
    return { ports: [...names].sort(byNumber).map((name) => ({ name })), states };
  }
  const layout = new Map(model.layout.map((l) => [l.port, l]));
  const declared = new Set(model.ports.map((p) => p.name));
  const ports: FacePort[] = model.ports.map((p) => {
    const l = layout.get(p.name);
    return l ? { name: p.name, kind: portKindOf(l.kind), row: l.row, col: l.col } : { name: p.name };
  });
  for (const name of [...names].filter((n) => !declared.has(n)).sort(byNumber)) {
    ports.push({ name });
    const detail = `${model.model} does not declare this port`;
    states[name] = { ...states[name], detail: states[name]?.detail ? `${states[name].detail}; ${detail}` : detail };
  }
  return { ports, states };
}

/** What a port's intent words are prefixed with in its detail (#1615). */
export const INTENT_LABEL = "planned in Design:";
/** What the switch's own report words are prefixed with in a port's detail. */
export const REPORT_LABEL = "switch reports:";

/** A model's ports as the faceplate takes them, from the catalogue alone:
 * every port the corpus declares, in its order, placed by the authored
 * layout where there is one and paired by the component's default otherwise.
 * The drawing of a device nobody has intent or a live read for — the far end
 * of a link (`Links.tsx`), a card in the catalogue page (#354). */
export function facePorts(model: DeviceModel): FacePort[] {
  const layout = new Map(model.layout.map((l) => [l.port, l]));
  return model.ports.map((p) => { const l = layout.get(p.name); return l ? { name: p.name, kind: portKindOf(l.kind), row: l.row, col: l.col } : { name: p.name }; });
}

/** The model's photo as the faceplate takes it, when the model has one. */
export function photoOf(model?: DeviceModel): FacePhoto | undefined {
  if (!model?.image) return undefined;
  return { src: modelImageURL(model.model, model.image.digest), width: model.image.width, height: model.image.height, boxes: model.image.boxes };
}

/** What the live read adds to a port's words beyond the marks the faceplate
 * paints itself (link, power): the speed, the neighbour, and each field the
 * switch did not answer with its reason. Nothing when the port answered
 * with a link and nothing more. */
export function liveWords(p: LivePort): string {
  const parts: string[] = [];
  if (p.link === "up" && p.speed_mbps !== undefined) parts.push(`${p.speed_mbps} Mb/s`);
  if (p.poe_draw_mw !== undefined && p.poe_draw_mw > 0) parts.push(`${p.poe_draw_mw / 1000} W delivered`);
  if (p.neighbour) parts.push(`neighbor ${neighbourWords(p.neighbour)}`);
  // A fact about the port, said after the neighbour and about neither it nor
  // what is on the other end (ADR-0088 decision 3).
  if (p.wan) parts.push("the site’s link to the WAN");
  // Restates ADR-0209 decision 6b — a member is marked with its aggregate, read from the
  // configuration the controller holds and not from the switch's running state (ADR-0078).
  if (p.aggregate) parts.push(`a member of ${p.aggregate} in the configuration the controller holds`);
  for (const u of p.unanswered) parts.push(`${u.field ?? "state"} unanswered: ${reasonWords(u.reason, u.field)}`);
  return parts.join(" · ");
}

/** A neighbour that matches no device of the site, named by what the switch
 * reports of it (#1480): its system name, else its management address, else
 * its MAC, and `rest` the other of the first two when it reported both. No
 * class phrase: the name is what the switch said, and it is never made a
 * device, a claim or a link (ADR-0045 decision 4). */
export function unknownNeighbour(n: LiveNeighbour): { title: string; rest: string[] } {
  const facts = [n.system_name, n.management_address].filter((x): x is string => !!x);
  return facts.length ? { title: facts[0]!, rest: facts.slice(1) } : { title: n.mac, rest: [] };
}

/** A neighbour in words: the site's device by name, or an unknown device by
 * what the switch reports of it. The column says the same with a link. */
export function neighbourWords(n: LiveNeighbour): string {
  const end = n.port ? ` on its ${n.port}` : "";
  if (n.in_site) return `${n.name ?? n.serial ?? n.mac}${end}`;
  const { title, rest } = unknownNeighbour(n);
  return `${title}${rest.length ? ` (${rest.join(", ")})` : ""}${end}`;
}

/** Each reason the API gives for a field it did not answer, in words that
 * say what the switch did rather than what the page could not do. On the
 * neighbour field alone, `unreadable` is an entry and not the report (#559):
 * the switch reports a neighbour here that this build cannot decode. */
export function reasonWords(reason: string, field?: string): string {
  switch (reason) {
    case "offline": return "the switch is offline and was not asked";
    case "no_report": return "the controller holds no state report for the switch";
    case "unreadable":
      return field === "neighbour"
        ? "the switch reports a neighbor in a shape this build cannot read"
        : "the switch's report could not be read";
    // Restates ADR-0266 decision 2 — with no held document a switch is answered from its report:
    // each port's name is the report's own key, unresolved, and no port is marked as the way out.
    // For the whole switch the word is what a reader that names ports by a configuration is told.
    case "no_configuration":
      if (field === "port") return "the controller holds no configuration for the switch, so this is the name the switch’s own report gives the port, and it is matched to no configuration";
      if (field === "wan") return "the controller holds no configuration for the switch, so the port it reaches its controller through cannot be identified";
      return "the controller holds no configuration for the switch, so its ports cannot be matched to the names a configuration gives them; the switch’s own Ports tab lists what its report says";
    case "silent": return "the report says nothing about it";
    case "vocabulary": return "the report spells its ports in a way this build does not recognize, and nothing is translated";
    case "unreported": return "none of the switch’s recent reports says which port it reaches its controller through";
    case "disagreed": return "the switch’s recent reports name different ports as the one it reaches its controller through, and nothing here chooses between them";
    case "unresolved": return "the switch reports a port its own configuration does not carry, so which port it reaches its controller through cannot be told";
    // Restates ADR-0209 decisions 1c and 4a — an uplink through an aggregate is every member of it, so
    // where a member's aggregate cannot be told no port is named and a plan refuses rather than
    // protecting a part.
    case "aggregated": return "the switch’s configuration makes a port a member of an aggregate whose members this build cannot all tell — no channel key, a pattern, or two kinds on one port — and an uplink through an aggregate is every member of it, so no port is named as the one it reaches its controller through and a plan is refused";
    // Restates ADR-0511 decision 5a — a reason this build does not know is named as received, marked.
    default: return `the switch did not answer it, for a reason this page does not recognize (${unrecognized(reason)})`;
  }
}

/** One port's intent as a sentence, for the faceplate's tooltip and the line
 * under it. The table says the same things in columns. With the site's tag
 * space, a tagged set reads by the customer's names (#590). */
export function describe(p: PortIntent, space?: Space): string {
  const parts = [p.mode === "trunk" || p.mode === "access" ? p.mode : unrecognized(p.mode)];
  if (p.untagged_vlan !== undefined) parts.push(`${p.mode === "trunk" ? "native" : "access"} VLAN ${p.untagged_vlan}`);
  if (p.tagged_vlans.length) parts.push(`tagged ${setWords(space, p.tagged_vlans)}`);
  const poe = poeWords(p.poe);
  if (poe) parts.push(`PoE ${poe}`);
  parts.push(p.enabled ? "enabled" : "disabled");
  if (p.description) parts.push(`“${p.description}”`);
  return parts.join(" · ");
}

/** What the port offers a powered device, in words; nothing when the intent
 * says nothing about it. An absent field is sent as the cell holds it
 * (ADR-0078 decision 1), which is not the same as off. */
export function poeWords(poe: PortIntent["poe"]): string | undefined {
  if (!poe) return undefined;
  const words = [poe.enabled === undefined ? undefined : poe.enabled ? "on" : "off", poe.priority, poe.power_limit_mw !== undefined ? `${poe.power_limit_mw / 1000} W` : undefined].filter(Boolean);
  return words.length ? words.join(", ") : undefined;
}

/** Every port the switch reaches its controller through: the one port, or
 * every member of the aggregate it is a member of (ADR-0209 decision 1c). A
 * server older than #1158 sends `uplink_port` alone, which is one member. */
export function uplinkMembers(set: Pick<PortSet, "uplink_port" | "uplink_members">): string[] {
  return set.uplink_members ?? (set.uplink_port ? [set.uplink_port] : []);
}

/** The uplink sentence under the faceplate. A switch reports its uplink by
 * the port's configured name when it has one (`Upstream`) and the API resolves
 * that to the port (`Ethernet23`) through the switch's own configuration; both
 * are shown when they differ, so the operator can see which port is meant and
 * what the switch calls it (#336). A report that resolved to no port is named
 * as such: the plan will refuse on it, and the faceplate marks nothing.
 *
 * Where the answer came from a report that is not the newest, the sentence
 * says so and when the switch wrote it (ADR-0079 decision 5). A switch whose
 * platform fails its default-gateway lookup names no uplink in the reports it
 * builds during a failure, and "the switch says Ethernet23" is a different
 * fact from "the switch said Ethernet23 four reports ago and has not answered
 * since" — the plan proceeds on both, and only one is worth looking into.
 *
 * Where no uplink is named the sentence says why, in the read's own words
 * (#1923): none of the reports read named one, the reports disagreed, the
 * controller holds no report, or the read went unanswered. It counts the
 * reports read and never says "never", because the read examines
 * `UPLINK_REPORT_WINDOW` reports and stores none (ADR-0079 decision 7). A
 * switch whose controller holds only a stub is said first, from the live read
 * the page already made: its port plan is refused before any uplink is read. */
function uplinkNote(set: PortSet, live?: LiveSet): ReactNode {
  const back = set.uplink_reports_back ?? 0;
  const wrote = set.uplink_reported_at ? new Date(set.uplink_reported_at) : undefined;
  const from = back > 0
    ? <>, from the report it wrote{wrote ? <> at {wrote.toLocaleTimeString()}</> : null} — {back === 1 ? "one report" : `${back} reports`} back, and it has named none since</>
    : null;
  // Restates ADR-0209 decision 1c — the uplink is the aggregate, every member of it, by the
  // configuration the controller holds (ADR-0078) and not by what the switch runs.
  if (set.uplink_aggregate && set.uplink_members?.length) {
    const said = set.uplink_reported ? <>, and it reports <code>{set.uplink_reported}</code> as its uplink</> : null;
    return <>; the switch reaches its controller through {set.uplink_aggregate} — {set.uplink_members.map((m, i) => <span key={m}>{i > 0 && (i === set.uplink_members!.length - 1 ? " and " : ", ")}<PortName name={m} /></span>)} in the configuration the controller holds, every one of them protected{said}{from}</>;
  }
  if (set.uplink_port) {
    const name = set.uplink_reported && set.uplink_reported !== set.uplink_port ? <>, which it calls <code>{set.uplink_reported}</code></> : null;
    return <>; the switch reports <PortName name={set.uplink_port} /> as its uplink{name}{from}</>;
  }
  // Restates ADR-0266 decision 2 and points at ADR-0215 and ADR-0555 — with no held document the switch is
  // answered from its report, so no port plan can be made, and its first document is the route, which
  // carries the planned settings where there are any (ADR-0555 decision 7a).
  // Pointer only: whether this switch's model and build can take one is the sheet's to say (ADR-0354).
  if (live?.keyed_by === "report") {
    const said = set.uplink_reported ? <>; it reports <code>{set.uplink_reported}</code> as its uplink</> : null;
    return <>; the controller holds no configuration for this switch, so a port plan is refused before any uplink is read{said}. Its first configuration, with any planned settings, is sent from Review &amp; apply on Design &rsaquo; Wired &rsaquo; Ports, which says whether this switch can take one</>;
  }
  if (set.uplink_reported) {
    return <>; the switch reports <code>{set.uplink_reported}</code> as its uplink, which is not a port of its configuration, so a plan is refused until the reported uplink is one</>;
  }
  const read = set.uplink_reports_read;
  const newest = set.uplink_newest_report_at ? new Date(set.uplink_newest_report_at) : undefined;
  const written = newest ? <>, the newest written at {newest.toLocaleString()}</> : null;
  // Restates ADR-0079 decision 3 — reports that disagree about the uplink answer with neither port.
  if (set.uplink_absent === "conflict" && set.uplink_conflict?.length) {
    const named = set.uplink_conflict.map((p, i) => <span key={p}>{i > 0 && (i === set.uplink_conflict!.length - 1 ? " and " : ", ")}<code>{p}</code></span>);
    return <>; {read ? <>the switch&rsquo;s last {read} {plural(read, "report")}</> : <>the switch&rsquo;s recent reports</>} name different uplinks, {named}, newest first, so none is taken and a plan is refused until they agree{written}</>;
  }
  // Restates ADR-0079 decisions 2 and 7 — the uplink is read across a window of reports that is
  // never stored, so an absence covers the reports read and nothing before them.
  if (set.uplink_absent === "none_named" && read) {
    return read === 1
      ? <>; its last report names no uplink{written}</>
      : <>; none of its last {read} reports names an uplink{written}</>;
  }
  if (set.uplink_absent === "no_report") {
    return "; the controller holds no state report for this switch, so no uplink is read";
  }
  if (set.uplink_absent === "unanswered") {
    return "; its uplink could not be read, because the controller did not answer";
  }
  return "; no uplink is read";
}

/** What the editor's values become for one port. VLANs typed as a list are
 * sent ascending and without repeats, the way the document describes the
 * field; a PoE choice left blank sends no `poe.enabled` at all, because
 * absent is sent as the cell holds it and false turns the port's power off. */
export function entryFromForm(v: FormValues, port: string, generation?: number): Entry {
  const tagged = [...new Set(text(v.tagged_vlans).split(",").map((s) => s.trim()).filter(Boolean).map(Number))].sort((a, b) => a - b);
  const poe: NonNullable<Entry["poe"]> = {};
  const choice = str(v.poe);
  if (choice === "on") poe.enabled = true;
  else if (choice === "off") poe.enabled = false;
  const priority = str(v.poe_priority);
  if (priority) poe.priority = priority as NonNullable<Entry["poe"]>["priority"];
  const limit = num(v.poe_power_limit_mw);
  if (limit !== undefined) poe.power_limit_mw = limit;
  const description = str(v.description);
  return {
    port, mode: text(v.mode) as Entry["mode"], untagged_vlan: num(v.untagged_vlan), tagged_vlans: tagged,
    ...(Object.keys(poe).length ? { poe } : {}), ...(description ? { description } : {}),
    enabled: Boolean(v.enabled), ...(generation !== undefined ? { generation } : {}),
  };
}

/** The editor's initial values for a port with intent. */
function formOf(p: PortIntent): FormValues {
  return {
    mode: p.mode, untagged_vlan: p.untagged_vlan, tagged_vlans: p.tagged_vlans.join(", "),
    poe: p.poe?.enabled === undefined ? "" : p.poe.enabled ? "on" : "off",
    poe_priority: p.poe?.priority ?? "", poe_power_limit_mw: p.poe?.power_limit_mw,
    description: p.description ?? "", enabled: p.enabled,
  };
}

export type HeldFields = components["schemas"]["SwitchPortFields"];
/** What the cell holds for this switch's ports, as the last read of it
 * answered (#571): the modelled fields of every port its document declares,
 * and when they were read — or why they could not be: the read's refusal, or
 * `why` where the read answered that the cell holds no document. `idle` is no
 * read made yet. Held, not running: it is the cell's copy (ADR-0078 decision 1). */
export type Held =
  | { status: "idle" | "reading" }
  | { status: "ready"; ports: Record<string, HeldFields>; readAt: Date }
  | { status: "unavailable"; problem?: Problem; why?: string };

const PRIORITY_VALUES = ["critical", "high", "medium", "low"];

/** The editor's initial values for a port with no intent, from the fields the
 * cell holds for it, and the labels of the fields the held document does not
 * say — those start at the form's defaults, and the editor names them rather
 * than letting a default read as a held value. The intent's `mode` is not a
 * field of the document: a port holding a tagged VLAN is a trunk, and any
 * other is an access port, which is the contract's own rule for the word. */
export function formOfHeld(f: HeldFields): { values: FormValues; unsaid: string[] } {
  const unsaid: string[] = [];
  if (f.enabled === undefined) unsaid.push("Enabled");
  const priority = f.poe?.priority;
  const carried = priority !== undefined && PRIORITY_VALUES.includes(priority);
  if (priority !== undefined && !carried) unsaid.push(`PoE priority (the configuration says \u201c${priority}\u201d, which planned settings cannot hold)`);
  return {
    values: {
      mode: f.tagged_vlans.length > 0 ? "trunk" : "access", untagged_vlan: f.untagged_vlan, tagged_vlans: f.tagged_vlans.join(", "),
      poe: f.poe?.enabled === undefined ? "" : f.poe.enabled ? "on" : "off",
      poe_priority: carried ? priority : "", poe_power_limit_mw: f.poe?.power_limit_mw,
      description: f.description ?? "", enabled: f.enabled ?? true,
    },
    unsaid,
  };
}


/** What the switch's drawing is, under the face at the head (#589): the
 * model it is drawn as or why every port or only the intent's are drawn, how
 * many ports intent describes, and the uplink sentence. */
export function SwitchFaceCaption({ set, model, live }: { set: PortSet; model?: DeviceModel; live?: LiveSet }) {
  return (
    <>
      {model && model.ports.length > 0
        ? <>Drawn as <code>{model.model}</code>, {model.ports.length} {plural(model.ports.length, "port")}; {set.ports.length} with planned settings</>
        : <>{set.ports.length} {plural(set.ports.length, "port")} with planned settings</>}
      {uplinkNote(set, live)}.
      {!(model && model.ports.length > 0) && (live && documentKeyed(live).ports.length > 0
        ? <>{" "}Every port the switch&rsquo;s configuration declares is drawn, because the switch was not matched to a catalog model.</>
        : <>{" "}Only the ports that have planned settings are drawn, because the switch was not matched to a catalog model.</>)}
    </>
  );
}

/** The live read's own line, the same on every tab of a switch's page (#514):
 * what the read is doing — reading, refused, unanswered with the switch's
 * reason, or answered and when the switch wrote the report — and the one
 * `Read again`, which reloads the page's single read so every tab sees the
 * same answer. The Ports tab said this alone while the sections were one
 * page (#371); once they became tabs (#388) the Links and Neighbourhood tabs
 * were drawn from the same read with nothing saying so. */
export function LiveLine({ live, times }: { live: ReturnType<typeof useLiveState>; times: ReportTimes }) {
  return (
    <p className="muted small live-line" aria-live="polite">
      {live.status === "loading" && <>Loading the switch&rsquo;s last port-state report from the controller&hellip;</>}
      {live.status === "error" && <>Port state could not be read: {live.problem.message}</>}
      {/* Restates ADR-0266 decision 2 — a switch with no held document answers from its report, under
          the report's own port names, and nothing of it is drawn on a face or matched to a port. */}
      {live.status === "ready" && (live.data.unanswered
        ? <>Port state unanswered: {reasonWords(live.data.unanswered.reason)}.</>
        : live.data.keyed_by === "report"
          ? <>Port state as the switch last reported it, under the report&rsquo;s own port names. The controller holds no configuration for this switch, so no live state is drawn on the faceplate or matched to a port. The Ports tab lists what the report says. <ReportWhen times={times} /></>
          : <>Port state as the switch last reported it: link, speed, power and neighbor per port. <ReportWhen times={times} /></>)}
      {" "}<button type="button" className="btn btn-quiet" onClick={live.reload} disabled={live.status === "loading"}>Read again</button>
    </p>
  );
}


// The plan's fixed sentences and the refusal reading moved to SwitchPlanFacts
// with the plan's rendering (#950); they are still read from here.
export { PLAN_IS_FROM_HELD, PLAN_IS_THE_ROUTE_BACK, explainRefusal } from "./SwitchPlanFacts";

/** Which of Design › Wired › Ports' sub-tabs is on screen (#1893): the ports with
 * their inspector and the plan, the switch-wide settings, or the settings
 * outside the plan. */
export type PortsSection = "ports" | "switch" | "advanced";

export default function SwitchPorts({ device, read, readStatus = "idle", set, face, tags, reload, writes, templates, onward, live, times, picked, onPick, plan, planReadAt, onPlan, mayReadPoE = false, section, review, pending }: {
  device: Device;
  /** The device's own read, which carries the switch's generation pair (#421), for the comparison (#369). */
  read?: Device;
  readStatus?: string;
  set: PortSet;
  /** The face the head draws (#589), computed once by the page from the same
   * reads: its ports are this tab's rows, in the drawing's order. */
  face: Face;
  /** The site's tag space (ADR-0064, #590), the page's one read: the table,
   * the port detail and the bulk editor's chooser read a tagged set by the
   * customer's names from it, as the face at the head does. */
  tags: TagSpace;
  /** Reads the intent again, which is what a stale refusal asks for. */
  reload(): void;
  /** The editor, the plan and the apply: present on Design › Wired's Ports
   * tab for a caller who holds `network.write` at the switch's site, absent
   * on a device's page, which reads (#875). */
  writes?: PortWrites;
  /** The port templates section, Design's alone (ADR-0127), drawn beside the
   * editor when there is one. */
  templates?: ReactNode;
  /** Where a reader goes to change what this shows: a device's page puts its
   * *Change this in Design* here, beside the section's title. */
  onward?: ReactNode;
  /** The live read, made once by the page (#371) and reloaded on request,
   * and when its answer was read and written — the page's too (#514), so
   * the time does not restart when a tab mounts. */
  live: ReturnType<typeof useLiveState>;
  times: ReportTimes;
  /** The set the face at the head gathers (#467, #473, #589): the page's,
   * pressed here by the table's name buttons with the same modifiers. */
  picked: readonly string[];
  onPick(next: string[]): void;
  /** The plan on screen and when it was read — the page's (#589), because
   * the change layer it paints is on the face at the head; made, discarded
   * and consumed here and nowhere else. */
  plan?: Plan;
  planReadAt?: Date;
  onPlan(plan: Plan | undefined): void;
  /** `device.cli.read` at the switch's site: whether the PoE column can be
   * filled from the switch's console (ADR-0264 decision 2b). */
  mayReadPoE?: boolean;
  /** Design › Wired's sub-tab (#1893). Left out — a device's page in Operate —
   * every section is drawn in one column, as before. */
  section?: PortsSection;
  /** The *Review & apply* sheet (#1891), Design's alone: absent on a device's page. */
  review?(props: ReviewProps): ReactNode;
  /** What the pending bar counts: the work list's item for this switch (#1891). */
  pending?: Pending;
}) {
  // Which port the editor is open for, if any: a name with intent edits it, a
  // name without adds intent for it, and "" adds intent for a port typed in.
  const [editing, setEditing] = useState<string>();
  const [removing, setRemoving] = useState<string>();
  const mayConfigure = writes !== undefined;
  // Whose switch the apply is aimed at (#1171), as the per-device carried
  // apply names it (#1168). Named from the switch's own site rather than the
  // scope in the address, because the site is what the apply writes into; the
  // customer is the one above that site. The estate is the copy the scope
  // rail reads, so this costs no request where the rail has already asked, and
  // it is asked only where there is an apply to confirm — a device's page in
  // Operate reads and draws none.
  const { lists } = useEstate(mayConfigure);
  const whose = scopeNames(lists, { siteId: set.site_id });
  const setPlan = onPlan;
  // The *Review & apply* sheet (#1891), opened from the pending bar.
  const [reviewing, setReviewing] = useState(false);
  const single = picked.length === 1 ? picked[0] : undefined;
  // Two lanes (#189): the single editor's, whose Problem the form shows
  // against its fields, and the bulk editor's, read beside its preview. The
  // plan's and the apply's are the sheet's (#1891). One lane would put a stale
  // save under the wrong form.
  const edit = useMutation();
  const bulk = useMutation();
  // The switch's own settings beside its ports (ADR-0369 decision 1a): their
  // own lane, so a refusal is said beside the voice VLAN and not a port form.
  const settingsLane = useMutation();
  // The command-line section's own lane (ADR-0417): a refusal there is not a save's.
  const cliLane = useMutation();
  // What the cell holds, for describing a port with no intent (#571): its own
  // lane, so a refused read is said in the editor that asked and not under
  // the plan button, where no plan was asked for.
  const heldLane = useMutation();
  const [heldRead, setHeldRead] = useState<Held>({ status: "idle" });
  // The live layer: one read when the page opens, and one more each time the
  // operator asks. Nothing here polls, and nothing is kept between reads.
  // This tab's rows are the face's ports, named as intent, the model and the
  // configuration name them. A report-keyed answer (ADR-0266) holds none of
  // those, so the rows and the port detail read it through `documentKeyed`
  // and its ports are listed in their own section below.
  const named = documentKeyedRead(live);
  const liveSet = named.status === "ready" ? named.data : undefined;
  const liveByName = new Map((liveSet?.ports ?? []).map((p) => [p.port, p]));
  const byName = new Map(set.ports.map((p) => [p.port, p]));
  const { ports } = face;
  const generations = Object.fromEntries(set.ports.map((p) => [p.port, p.generation]));
  const members = uplinkMembers(set);
  const rows = ports.map((p) => ({ name: p.name, intent: byName.get(p.name), uplink: members.includes(p.name) }));
  // A report keyed by its own names (ADR-0266): its ports end the table under
  // those names, and the two marks every entry carries — the port unresolved,
  // the way out unknown — are said once above it rather than on every row.
  const reported = live.status === "ready" && live.data.keyed_by === "report" ? live.data : undefined;
  const reportRows = reported && !reported.unanswered
    ? [...reported.ports].sort((a, b) => byNumber(a.port, b.port))
        .map((p): LivePort => ({ ...p, unanswered: p.unanswered.filter((u) => u.field !== "port" && u.field !== "wan") }))
    : [];
  // Whether a row leaves a cell empty that the line above the table explains (#1801).
  const unplanned = rows.some((r) => !r.intent);
  // Only once the read has answered: a read still out, or failed, keeps its
  // marks, and the line under the face says which.
  const quietLive = (named.status === "ready" && rows.length > 0) || reportRows.length > 0;
  // Where the switch's own report and the held document disagree about a
  // port's native VLAN (#574). Empty for every switch that reports nothing
  // out of step, which draws nothing — silence, not a tick.
  const vlanFaults = vlansByPort(set.vlan_disagreement);
  // The console's PoE reading, when an operator asked for one: shown beside the
  // report's draw with its own time, and kept only until the next read or until
  // the page goes (ADR-0264 decision 3a).
  const [poe, setPoe] = useState<PoEReading>();
  // The inspector's fields, held here so the pending bar can count them and
  // its *Discard* can drop them (#1893).
  const [bulkEdit, setBulkEdit] = useState<BulkEdit>(EMPTY_EDIT);
  const [heldOpen, setHeldOpen] = useState(false);
  // Whether the open port form holds a value that differs from the saved one.
  const [editDirty, setEditDirty] = useState(false);

  // The whole set, every time: a port present is created or updated with the
  // generation it was read at, and a port left out has its intent removed.
  // The lane is the caller's, so the refusal lands beside the form that sent it.
  const put = async (next: Entry[], lane = edit, extra?: Parameters<PortWrites["put"]>[2]) => {
    if (!writes) return false;
    const ok = await lane.run(() => writes.put(device.serial, next.sort((a, b) => byNumber(a.port, b.port)), extra));
    if (ok) {
      // A plan on screen was computed from the intent before this save. The
      // API would refuse its digest as stale; it is put away here so the apply
      // button is never beside a document the save just made untrue.
      setPlan(undefined);
      setEditing(undefined); setRemoving(undefined);
      reload();
    }
    return ok;
  };
  // The plan on screen is the freshest reading of the held document there is,
  // and the one the operator is looking at; without one, the last read made
  // for an editor.
  const held: Held = plan?.changes
    ? { status: "ready", ports: Object.fromEntries(plan.changes.map((c) => [c.port, c.held])), readAt: planReadAt ?? new Date() }
    : heldRead;
  // The held document, read for an editor without a plan (#2387): a plan on a measured pair reads the
  // switch's console, and opening an editor is not an act that should hold the switch's one session
  // against the operator's own read, plan or apply. This read asks the cell and nothing else.
  const readHeld = async () => {
    if (!writes) return;
    setHeldRead({ status: "reading" });
    const r = await heldLane.runWith(() => writes.readHeld(device.serial));
    if (r.ok && r.data?.held === "document") setHeldRead({ status: "ready", ports: Object.fromEntries(r.data.ports.map((p) => [p.port, p.held])), readAt: new Date() });
    else if (r.ok && r.data) setHeldRead({ status: "unavailable", why: r.data.held === "stub" ? "the controller holds only a stub for this switch, with no configuration to read its ports from" : "the controller holds no configuration for this switch" });
    else setHeldRead({ status: "unavailable" });
  };
  const needHeld = () => { if (heldRead.status === "idle" && !plan?.changes) void readHeld(); };
  const heldForEditors: Held = held.status === "unavailable" ? { status: "unavailable", problem: heldLane.problem, why: held.why } : held;
  const save = (port: string, v: FormValues) => {
    const current = byName.get(port);
    const rest = set.ports.filter((p) => p.port !== port).map(entryOf);
    return put([...rest, entryFromForm(v, port, current?.generation)]);
  };
  const remove = (port: string) => put(set.ports.filter((p) => p.port !== port).map(entryOf));
  // One set, the page's.
  const pick = onPick;
  const order = ports.map((p) => p.name);
  const openEditor = (port: string | undefined) => {
    edit.clear(); setRemoving(undefined); setEditing(port); if (port) pick([port]);
    // A port with no intent is described from what the cell holds (#571),
    // read anew each time such an editor opens — the editor asks for it —
    // unless a plan on screen already says it. A port typed in by name has no
    // name to read for yet.
    if (port && !byName.has(port)) setHeldRead({ status: "idle" });
  };
  // A row's name button presses the same thing the faceplate does, with the
  // same modifiers; a plain press on the one port held clears the set.
  const pressRow = (name: string, e: { shiftKey: boolean; ctrlKey: boolean; metaKey: boolean }) => {
    if (modeOf(e) === "replace" && single === name) pick([]);
    else pick(applySelection(picked, [name], modeOf(e), order));
  };
  const rowPressed = (name: string) => picked.includes(name);

  const editorNode = editing !== undefined && (
    <PortEditor key={editing} port={editing} intent={editing ? byName.get(editing) : undefined} lane={edit}
      held={heldForEditors} onNeedHeld={needHeld} onReadHeld={() => void readHeld()} onDirty={setEditDirty}
      onSave={save} onCancel={() => { edit.clear(); setEditing(undefined); }} onReload={() => { edit.clear(); reload(); }} />
  );
  const design = section !== undefined;
  // The ports whose fields differ from their saved values (#1893, #1891): the
  // bulk editor's own preview, so the bar and the preview are one computation,
  // and the port a single form is open for once a field of it differs from
  // what was saved. An open form with nothing changed is not unsaved.
  const heldPorts = held.status === "ready" ? held.ports : undefined;
  const unsaved = [...new Set([
    ...(isEmptyEdit(bulkEdit) ? [] : composeSet(set, picked, bulkEdit, heldPorts).rows.filter((r) => r.differs.length > 0).map((r) => r.port)),
    ...(editing && editDirty ? [editing] : []),
  ])];
  const discard = () => { setBulkEdit(EMPTY_EDIT); bulk.clear(); edit.clear(); setEditing(undefined); setRemoving(undefined); };
  // *Review & apply* opens the one act (#1891).
  const openReview = () => setReviewing(true);
  const emptyCells = (unplanned || quietLive) && (
    <>
      {unplanned && <>A port with nothing under {design ? "Mode, VLANs, PoE and Description" : "Mode, VLANs, PoE, Enabled and Description"} has no planned settings. Applying a plan sends it as the controller holds it. </>}
      {quietLive && (!reported
        ? design
          ? <>An empty Link or Neighbor cell is one the switch&rsquo;s last report says nothing about.</>
          : <>An empty Link, Neighbor or Power cell is one the switch&rsquo;s last report says nothing about. An empty Power cell is not 0 W.</>
        : reportRows.length === 0
          ? <>Link, Neighbor and Power are empty because nothing matches these ports to the switch&rsquo;s report.</>
          : <>Link, Neighbor and Power are empty on the rows above the report&rsquo;s own, because nothing matches those ports to the report. On the report&rsquo;s rows, an empty cell is one the report says nothing about. An empty Power cell is not 0 W.</>)}
    </>
  );
  const portDetail = single && (
    <PortDetail siteId={set.site_id} serial={device.serial} port={single} intent={byName.get(single)} plan={plan} generations={generations} space={tags.space} vlan={vlanFaults.get(single)} ownActs={!design}
      live={named} times={times} mayConfigure={mayConfigure} editing={editing} removing={removing === single} busy={edit.busy}
      onEdit={() => openEditor(single)} onRemove={() => { edit.clear(); setEditing(undefined); setRemoving(single); }}
      onKeep={() => setRemoving(undefined)} onConfirmRemove={() => void remove(single)} />
  );
  // The selected port's intent acts, at the inspector's head on Design (#1891):
  // the compact table carries none, so every edit of a port is here.
  const portActs = single && mayConfigure && (
    <PortActions port={single} intent={byName.get(single)} mayConfigure={mayConfigure} editing={editing} removing={removing === single} busy={edit.busy}
      onEdit={() => openEditor(single)} onRemove={() => { edit.clear(); setEditing(undefined); setRemoving(single); }}
      onKeep={() => setRemoving(undefined)} onConfirmRemove={() => void remove(single)} />
  );
  // What an empty cell is, said once beside the title rather than on every row (#1801). It
  // says what an apply sends for a port with no planned settings, so it sits behind the ⓘ
  // beside the table it describes (#1890, ruled 2026-10-20).
  const portsTitle = emptyCells ? <span className="page-title"><h2>Ports</h2><Info label="About empty cells">{emptyCells}</Info></span> : <h2>Ports</h2>;
  const portsPanel = (
    <section className="panel" aria-label="Ports">
      {onward ? <div className="page-head">{portsTitle}{onward}</div> : portsTitle}
      {/* Said on the read, before any port is described (#1072): until
          then a switch no plan can ever reach drew an ordinary table and
          the refusal arrived only after a plan was asked for. The server's
          answer, not a guess here — `unknown` says nothing either way. */}
      {set.firmware_qualified === "unqualified" && (
        <div className="notice" role="note" aria-label="Firmware">
          <strong>This switch&rsquo;s firmware has no qualified schema.</strong>{" "}
          {unpinnedFirmwareSentence(set.firmware)}
        </div>
      )}
      {/* A switch whose controller holds no configuration (ADR-0266): what
          that means for the rows at the table's end, said once (#1801). */}
      {reported && <ReportKeyedNotice set={reported} />}
      {rows.length === 0 && reportRows.length === 0
        ? <p className="muted">This switch has no planned port settings and no uplink was read for it, so there is nothing to draw yet.</p>
        : (
          <>
            {/* The face is at the head of the page (#589); the set it
                gathers is what the editor describes and the table marks.
                On Design's Ports sub-tab the editor and the port's detail
                are the inspector's, drawn only for a selection (#1893). */}
            {!design && mayConfigure && rows.length > 0 && (
              <PortsEditor set={set} picked={picked} uplink={members} lane={bulk} drawn={ports.length}
                onApply={(entries) => put(entries, bulk)} onReload={() => { bulk.clear(); reload(); }}
                onSelectAll={() => pick(order)} onClear={() => pick([])}
                held={heldForEditors} onNeedHeld={needHeld} tags={tags} />
            )}
            {!design && portDetail}
            {/* The editor opens beside the port it is for (#367); one for a
                port that is not the selected one stays under the table. */}
            {!design && mayConfigure && editing !== undefined && editing === single && editorNode}
            {/* The console's PoE reading fills the Power column, which Design's compact table does not draw (#1891). */}
            {!design && mayReadPoE && <PoERead device={device} reading={poe} onRead={setPoe} />}
            {design && mayConfigure && picked.length === 0 && rows.length > 0 && (
              <div className="actions"><button type="button" className="btn btn-quiet" onClick={() => pick(order)}>Select all {ports.length} {plural(ports.length, "port")}</button></div>
            )}
            {/* One table for the switch's ports (#1801): the planned settings and
                the switch's own report are columns of one row, matched by port, and
                a report keyed by its own names is listed under those names at the
                end, matched to nothing (ADR-0266). */}
            <div className="table-wrap"><table className="ports" aria-label="Ports">
              {/* Design's table is compact (#1891): no Power, Enabled or action column, and a
                  row's click selects its port, whose inspector does every edit. */}
              <thead>{design
                ? <tr><th>Port</th><th>Link</th><th>Neighbor</th><th>Mode</th><th>VLANs</th><th>PoE</th><th>Description</th></tr>
                : <tr><th>Port</th><th>Link</th><th>Neighbor</th><th>Power</th><th>Mode</th><th>VLANs</th><th>PoE</th><th>Enabled</th><th>Description</th><th aria-label="Actions" /></tr>}</thead>
              <tbody>{rows.map((r) => (
                <tr key={r.name} className={rowPressed(r.name) ? (design ? "is-selected is-pickable" : "is-selected") : design ? "is-pickable" : undefined} aria-selected={rowPressed(r.name)}
                  onClick={design ? (e) => { if (!(e.target as Element).closest("button, a")) pressRow(r.name, e); } : undefined}>
                  <td>
                    {/* Named "Select …" so it is not a second button called
                        Ethernet3 beside the faceplate's; the two press together. */}
                    <button type="button" className="btn btn-quiet port-name" aria-label={`Select ${r.name}`} aria-pressed={rowPressed(r.name)} onClick={(e) => pressRow(r.name, e)}><PortName name={r.name} /></button>
                    {r.uplink && <> <Pill tone="warn" title="The port the switch reports it reaches its controller through. The plan refuses planned settings that would cut it off.">uplink</Pill></>}
                    {design && r.intent && !r.intent.enabled && <> <Pill tone="serious">disabled</Pill></>}
                  </td>
                  <td><LinkCell status={named.status} port={liveByName.get(r.name)} quiet={quietLive} /></td>
                  <td><NeighbourCell status={named.status} port={liveByName.get(r.name)} quiet={quietLive} /></td>
                  {!design && <td><PoECell status={named.status} port={liveByName.get(r.name)} reportedAt={times.reportedAt} name={r.name} reading={poe} quiet={quietLive} /></td>}
                  {r.intent ? (
                    <>
                      <td>{r.intent.mode}</td>
                      <td>
                        {r.intent.untagged_vlan !== undefined && <>{r.intent.mode === "trunk" ? "native" : "access"} {r.intent.untagged_vlan}</>}
                        {r.intent.untagged_vlan !== undefined && r.intent.tagged_vlans.length > 0 && <br />}
                        {r.intent.tagged_vlans.length > 0 && <span title={runsWords(r.intent.tagged_vlans)}>tagged {setWords(tags.space, r.intent.tagged_vlans)}</span>}
                        {r.intent.untagged_vlan === undefined && r.intent.tagged_vlans.length === 0 && <span className="muted">—</span>}
                        <PortVlanFlag d={vlanFaults.get(r.name)} />
                      </td>
                      <td>{poeWords(r.intent.poe) ?? <span className="muted">—</span>}</td>
                      {!design && <td>{r.intent.enabled ? "yes" : <Pill tone="serious">no</Pill>}</td>}
                      <td>{r.intent.description ?? <span className="muted">—</span>}</td>
                    </>
                  ) : (
                    <td colSpan={design ? 4 : 5} />
                  )}
                  {!design && <td className="row-actions">
                    {mayConfigure && r.intent && removing !== r.name && <button type="button" className="btn btn-quiet" onClick={() => openEditor(r.name)}>Edit planned settings</button>}
                    {mayConfigure && r.intent && removing !== r.name && <button type="button" className="btn btn-quiet" onClick={() => { edit.clear(); setEditing(undefined); setRemoving(r.name); }}>Remove planned settings</button>}
                    {mayConfigure && r.intent && removing === r.name && (
                      <>
                        <span className="muted">Remove this port&rsquo;s planned settings? Applying a plan then sends it as the controller holds it.</span>{" "}
                        <button type="button" className="btn btn-danger" disabled={edit.busy} onClick={() => void remove(r.name)}>Remove {r.name}&rsquo;s planned settings</button>
                        <button type="button" className="btn btn-quiet" onClick={() => setRemoving(undefined)}>Keep</button>
                      </>
                    )}
                    {mayConfigure && !r.intent && <button type="button" className="btn btn-quiet" onClick={() => openEditor(r.name)}>Describe</button>}
                  </td>}
                </tr>
              ))}</tbody>
              {reportRows.length > 0 && (
                // Restates ADR-0266 decisions 1 and 2 — each port keyed by the report's own key, carried
                // verbatim and unresolved: no name here is a row above, however it is spelt.
                <tbody aria-label="Ports as the switch reports them">
                  <tr className="ports-group"><th colSpan={design ? 7 : 10} scope="rowgroup">As the switch&rsquo;s own report names them, matched to no configuration</th></tr>
                  {reportRows.map((p) => (
                    <tr key={`report:${p.port}`}>
                      <td><code>{p.port}</code></td>
                      <td><LinkCell status="ready" port={p} quiet /></td>
                      <td><NeighbourCell status="ready" port={p} quiet /></td>
                      {/* No console reading is passed: the console names ports in a configuration's
                          vocabulary, and nothing matches that to a report key. */}
                      {!design && <td><PoECell status="ready" port={p} reportedAt={times.reportedAt} name={p.port} quiet /></td>}
                      <td colSpan={design ? 4 : 6} />
                    </tr>
                  ))}
                </tbody>
              )}
            </table></div>
          </>
        )}
      {mayConfigure && editing === undefined && <div className="actions"><button type="button" className="btn" onClick={() => openEditor("")}>Add a port</button></div>}
      {/* The remove path's refusal has no form to show it in. */}
      {editing === undefined && edit.problem && <ProblemBox problem={edit.problem} onDismiss={edit.clear} />}
      {mayConfigure && editing !== undefined && editing !== single && editorNode}
    </section>
  );
  // The selection's inspector (#1893): the bulk editor's fields for one port
  // or many, the single port's form when one is open, and what the cell holds
  // beside what is planned behind a disclosure.
  const inspector = design && picked.length > 0 && (
    <Inspector picked={picked} onClose={() => pick([])}>
      {portActs}
      {mayConfigure && (
        <PortsEditor set={set} picked={picked} uplink={members} lane={bulk} drawn={ports.length}
          onApply={(entries) => put(entries, bulk)} onReload={() => { bulk.clear(); reload(); }}
          onSelectAll={() => pick(order)} onClear={() => pick([])}
          held={heldForEditors} onNeedHeld={needHeld} tags={tags} edit={bulkEdit} onEdit={setBulkEdit} />
      )}
      {/* Open while the port's own form is, which follows its detail (#367). */}
      <details className="learn-more" open={heldOpen || (editing !== undefined && editing === single)} onToggle={(e) => setHeldOpen(e.currentTarget.open)}>
        <summary>Held and planned</summary>
        <div className="learn-more-body">
          {portDetail}
          {mayConfigure && editing !== undefined && editing === single && editorNode}
          {/* Design's alone (#1801): its ports block is a plan's, and a plan is made
              here, beside the editor. A device's page in Operate says the held and
              running pair once, in its Configuration section. */}
          {mayConfigure && <Compared device={device} read={read} readStatus={readStatus} plan={plan} planned selected={single} onSelect={(p) => pick([p])} />}
        </div>
      </details>
    </Inspector>
  );
  // Keyed by the intent the read gave, so a save's reload starts the form
  // from what was stored rather than from what was typed.
  const settings = (
    <SwitchSettings key={settingsKey(set)} set={set}
      lane={mayConfigure ? settingsLane : undefined}
      onSave={mayConfigure ? ({ ports: next, ...extra }) => put(next, settingsLane, extra) : undefined} />
  );
  const cli = writes?.applyCliSetting && <SwitchCliSettings serial={device.serial} apply={writes.applyCliSetting} read={writes.readCliAdmission} lane={cliLane} />;
  // The one act (#1891): Design's alone, where the writes are.
  const sheet = writes && review && reviewing && review({
    plan, onPlan: setPlan, mayRead: mayReadPoE, whose, space: tags.space, onClose: () => setReviewing(false), onImported: reload,
    onApplied: () => { setHeldRead({ status: "idle" }); reload(); }, onPick: (p) => pick([p]), intentPorts: set.ports.length,
  });

  // Design › Wired's sub-tabs (#1893): one section at a time, the component
  // kept mounted across them so a plan, a lane's refusal and the inspector's
  // fields survive a look at another tab.
  if (section === "switch") return <>{settings}{mayConfigure && templates}</>;
  if (section === "advanced") {
    return (
      <>
        <p className="subtab-flag"><Pill tone="warn" title="These settings are not part of the plan. Each is applied on its own act, by the switch's command line.">Outside the plan</Pill></p>
        {cli}
      </>
    );
  }
  if (section === "ports") {
    return (
      <>
        <div className={inspector ? "ports-layout has-inspector" : "ports-layout"}>
          {portsPanel}
          {inspector}
        </div>
        {mayConfigure && <PendingBar pending={pending} unsaved={unsaved} onDiscard={discard} onReview={review ? openReview : undefined} reviewing={reviewing} />}
        {sheet}
      </>
    );
  }
  return (
    <>
      {portsPanel}
      {settings}
      {cli}
      {mayConfigure && templates}
      {mayConfigure && <Compared device={device} read={read} readStatus={readStatus} plan={plan} planned selected={single} onSelect={(p) => pick([p])} />}
    </>
  );
}

const unitWatts = (mw: number) => `${(mw / 1000).toFixed(mw < 10000 ? 1 : 0)} W`;

/** A switch's unit PoE in words, from its report's unit block (#878): what it
 * delivers against its budget, or which of the two the report did not carry.
 * Never 0 W for a report that said nothing (conventions/dashboards-as-code.md). */
export function unitPoEWords(poe: LiveSet["poe"]): string {
  const used = poe?.consumed_mw, budget = poe?.budget_mw;
  if (used !== undefined && budget !== undefined) return `${unitWatts(used)} delivered of a ${unitWatts(budget)} budget`;
  if (used !== undefined) return `${unitWatts(used)} delivered; the report carries no budget`;
  if (budget !== undefined) return `a budget of ${unitWatts(budget)}; the report does not say what the unit delivers`;
  return "not reported; the switch’s report carries no PoE figure for the unit";
}

/** What a switch whose cell holds no configuration document is answered with
 * (#1429, ADR-0266 decision 2), said once above its ports table: its ports
 * are the switch's own report's, the unit's PoE is the report's, and the rows
 * at the table's end are those ports under the report's own names (#1801).
 *
 * **Those rows are never made into the table's other rows.** A report key is
 * carried exactly as the switch spelt it and is resolved against nothing, so
 * it is shown as text: no faceplate port, no selection, no intent, no link and
 * no console reading is matched to it, even where a model's port or an
 * intent's is spelt the same way. That is why the port and the way out, which
 * every entry carries as unanswered, are said once here and stripped from each
 * row: a column repeating them would be the same sentence on every line.
 *
 * Renders nothing until the read has answered; the read's own line at the head
 * says what it is doing. */
function ReportKeyedNotice({ set }: { set: LiveSet }) {
  return (
    <>
      {/* Restates ADR-0266 decisions 1 and 2 — a switch whose cell holds no document, a stub included,
          is answered from its report alone: each port keyed by the report's own key, carried verbatim,
          unresolved against any document, possibly twice under two spellings, and none marked `wan`. */}
      <div className="notice" role="note" aria-label="Unresolved ports">
        <strong>The controller holds no configuration for this switch.</strong>
        <Info label="About the reported ports">Its ports are listed at the end of the table below from the switch&rsquo;s own state report, each under the name the report gives it. No name there is matched to a configuration, so it is not a port of the faceplate above, of the planned settings or of a link. A port the report spells in two ways is listed twice, and no port is marked as the site&rsquo;s WAN port.</Info>
      </div>
      {set.unanswered
        ? <p className="muted">The switch&rsquo;s report was not read: {reasonWords(set.unanswered.reason)}. There are no reported ports to list. That does not mean the switch has no ports.</p>
        : (
          <>
            <p>Unit PoE: {unitPoEWords(set.poe)}.</p>
            {set.ports.length === 0 && <p className="muted">The report names no port in its link or neighbor tables.</p>}
          </>
        )}
    </>
  );
}

/** One port's link as the live read answered it: up with its speed, down, or
 * why it is not known. A dash while the read is still out or failed — the
 * line under the faceplate says which. */
export function LinkCell({ status, port, quiet = false }: { status: string; port?: LivePort; quiet?: boolean }) {
  if (status === "loading") return <span className="muted">&hellip;</span>;
  // In the ports table, which says once above it what an empty cell is (#1801).
  if (!port) return quiet ? null : <span className="muted">—</span>;
  const u = port.unanswered.find((x) => x.field === "link");
  if (u && quiet && u.reason === "silent") return null;
  if (u) return <span className="muted" title={reasonWords(u.reason)}>unanswered · {u.reason}</span>;
  if (port.link === "up") return <><Pill tone="good">up</Pill>{port.speed_mbps !== undefined && <> {port.speed_mbps} Mb/s</>}</>;
  if (port.link === "down") return <span>down</span>;
  return <span className="muted">—</span>;
}

/** One port's neighbour: the site's device as a link to it, or an unknown
 * device by what the switch reports of it, for display only — never a
 * device, a claim or a link (ADR-0045 decision 4).
 *
 * Where the port is the one the switch reaches its cell through, the cell
 * says so (ADR-0088) — which is what stops the site's own way out reading
 * exactly like a neighbour Zenith failed to recognise. **The words describe
 * the port, never the peer**: "on the site's link to the WAN" says where this
 * neighbour sits, and asserts nothing about what it is. `in_site` stays false
 * and everything shown beside it is still only what the switch reported. */
export function NeighbourCell({ status, port, quiet = false }: { status: string; port?: LivePort; quiet?: boolean }) {
  if (status === "loading") return <span className="muted">&hellip;</span>;
  if (!port) return quiet ? null : <span className="muted">—</span>;
  // The mark, or the refusal in its place. The refusal is drawn here and not
  // only in the faceplate's line, because decision 4's requirement is that a
  // port whose mark is unanswered must not be distinguishable from an ordinary
  // port only by the reader's inference — and in this table an absent mark is
  // exactly that inference.
  const refused = port.unanswered.find((x) => x.field === "wan");
  const wan = port.wan
    ? <span className="dim" title="The port the switch reports it reaches its controller through. This says where the neighbor sits; it says nothing about what the neighbor is."> · on the site&rsquo;s link to the WAN</span>
    : refused
      ? <span className="muted" title={`Which port is the site’s WAN port: ${reasonWords(refused.reason, refused.field)}.`}> · WAN port unanswered &middot; {refused.reason}</span>
      : null;
  const u = port.unanswered.find((x) => x.field === "neighbour");
  // A report silent about the neighbor says nothing, and the table says so once (#1801).
  if (u && quiet && u.reason === "silent") return wan;
  if (u) return <span className="muted" title={reasonWords(u.reason, u.field)}>unanswered · {u.reason}</span>;
  const n = port.neighbour;
  if (!n) return <>{wan ?? <span className="muted">none</span>}</>;
  const end = n.port ? <span className="dim"> · its <code>{n.port}</code></span> : null;
  if (n.in_site && n.serial) return <><DeviceRef serial={n.serial} name={n.name}>{n.name ?? n.serial}</DeviceRef>{wan}{end}</>;
  const who = unknownNeighbour(n);
  return (
    <>
      <span title="The MAC the switch reports matches no device of this site; this is what the neighbor says of itself.">{who.title}</span>
      {wan}
      {who.rest.length > 0 && <span className="dim"> · {who.rest.join(", ")}</span>}
      {end}
    </>
  );
}

/** What a blank first-intent form is, said wherever one is drawn (#571). */
const BLANK_IS_NOT_THE_PORT = "The form below is blank. It is not what the port has. If you save it, it becomes all of the port\u2019s planned settings, and the next plan you apply replaces the port\u2019s VLANs and PoE with it.";

const MODES = [{ value: "access", label: "access" }, { value: "trunk", label: "trunk" }];
const POE = [{ value: "on", label: "on" }, { value: "off", label: "off — turns the port's power off" }];
const PRIORITIES = ["critical", "high", "medium", "low"].map((p) => ({ value: p, label: p }));

/** One port's editor. The values are the operator's until saved; a stale
 * refusal keeps them and offers to read the set again, so the retry the
 * document asks for carries the new generation and the same edits. */
function PortEditor({ port, intent, held, lane, onSave, onCancel, onReload, onNeedHeld, onReadHeld, onDirty }: {
  /** The port being edited, or "" for one being named. */
  port: string;
  intent?: PortIntent;
  /** What the cell holds for the switch's ports: where a port with no intent
   * starts from (#571). Not read for a port that has intent. */
  held: Held;
  /** Asks for the read when none has been made; `onReadHeld` reads again. */
  onNeedHeld(): void;
  onReadHeld(): void;
  lane: ReturnType<typeof useMutation>;
  onSave(port: string, v: FormValues): Promise<boolean>;
  onCancel(): void;
  onReload(): void;
  /** Told whether a field differs from the form's saved value (#1891). */
  onDirty(dirty: boolean): void;
}) {
  const naming = port === "";
  // A port with no intent starts from what the cell holds for it. While that
  // is being read there is no form: one drawn now would be the blank one, and
  // a form's values are the operator's from the moment it is drawn.
  const first = !intent && !naming;
  const reading = first && (held.status === "reading" || held.status === "idle");
  const wantsHeld = first && held.status === "idle";
  useEffect(() => { if (wantsHeld) onNeedHeld(); }, [wantsHeld, onNeedHeld]);
  const heldFields = first && held.status === "ready" ? held.ports[port] : undefined;
  const start = heldFields ? formOfHeld(heldFields) : undefined;
  const initial: FormValues = intent ? formOf(intent) : start ? start.values : { mode: "access", enabled: true, tagged_vlans: "", poe: "", poe_priority: "", description: "" };
  // Unsaved is a field that differs from the saved value, read through the
  // same mapping a save sends, so a value typed and typed back is not unsaved.
  const differs = (v: FormValues) => JSON.stringify(entryFromForm({ ...v, port: undefined }, text(v.port))) !== JSON.stringify(entryFromForm(initial, ""));
  useEffect(() => () => onDirty(false), [onDirty]);
  return (
    <div className="panel port-editor">
      {/* Where the form's values come from, and the route back when the switch has moved, sit
          behind the ⓘ beside its title (#1890), each sentence whole. */}
      <span className="page-title"><h3>{intent ? <>Edit <code>{port}</code></> : naming ? "Describe a port" : <>Describe <code>{port}</code></>}</h3>
        {((start && held.status === "ready") || intent) && (
          <Info label="About this form">
            {start && held.status === "ready" && (
              <>
                These values are <strong>held</strong> by the controller for <code>{port}</code>: read at {held.readAt.toLocaleTimeString()} from the configuration the controller holds for this switch, which is not necessarily what the switch runs.
                When you save, they become the port&rsquo;s planned settings, with whatever you change. A field you leave as it is stays as the controller holds it.
                {start.unsaid.length > 0 && <> The configuration does not say these, so the form&rsquo;s own default is used: {start.unsaid.join("; ")}.</>}
                {" "}A VLAN the port has through a range or a wildcard is not listed here. A plan&rsquo;s warnings name it.{intent && " "}
              </>
            )}
            {/* The Hotel Les Cigales case (#572): intent is right, the switch is
                not, and the form already holds the value, so the editor has nothing
                to offer. The way back is the plan, and this is where an operator
                looking for it arrives. */}
            {intent && <>If the switch no longer matches these planned settings, for example because a VLAN was taken off the port at its console, there is nothing to change here: the planned settings already say it. Review and apply the ports with nothing changed. It re-sends the configuration the controller holds and puts the switch back, on every port at once.</>}
          </Info>
        )}
      </span>
      {reading && (
        <>
          <p className="muted" aria-live="polite">Loading what the controller holds for <code>{port}</code>&hellip;<Info label="About this read">A plan is what reads it, and a plan sends nothing.</Info></p>
          <div className="actions"><button type="button" className="btn" onClick={onCancel}>Cancel</button></div>
        </>
      )}
      {first && held.status === "ready" && !heldFields && (
        <p className="refusal-why" role="alert">
          The configuration the controller holds for this switch declares no port named <code>{port}</code>, so there is nothing held to start from. {BLANK_IS_NOT_THE_PORT}
        </p>
      )}
      {first && held.status === "unavailable" && (
        <p className="refusal-why" role="alert">
          What the controller holds for <code>{port}</code> could not be read: {held.problem?.message ?? held.why ?? "the read did not list the switch\u2019s ports"}. {BLANK_IS_NOT_THE_PORT}{" "}
          <button type="button" className="btn btn-quiet" onClick={onReadHeld}>Read it again</button>
        </p>
      )}
      {naming && (
        <p className="refusal-why" role="alert">
          This form is blank. It is not what the controller holds for the port you name. If you save it, it becomes all of that port&rsquo;s planned settings, and the next plan you apply replaces the port&rsquo;s VLANs and PoE with it. To start from what the controller holds, use <strong>Describe</strong> on the port&rsquo;s own row.
        </p>
      )}
      {!reading && <SpecForm key={start ? "held" : "blank"} fields={[
        ...(naming ? [{ name: "port", label: "Port", required: true, pattern: "^\\S{1,64}$", hint: "As the switch names it, for example Ethernet0. The plan refuses a name the switch's configuration does not have." }] : []),
        { name: "mode", label: "Mode", type: "select", required: true, options: MODES },
        { name: "untagged_vlan", label: "Untagged VLAN", type: "number", hint: "1 to 4094: the access VLAN on an access port, the native VLAN on a trunk. At most one." },
        { name: "tagged_vlans", label: "Tagged VLANs", pattern: "^\\s*$|^\\s*\\d{1,4}(\\s*,\\s*\\d{1,4})*\\s*$", hint: "Comma-separated, for example 10, 20, 30. If the switch has no interface for a VLAN, the plan creates one and names it." },
        // Restates ADR-0078 decision 1 — a blank PoE field is carried from the document the controller
        // holds (`internal/generate/switchapply.go`), which replaces a PoE setting changed on the switch
        // by hand. Until #1678 the hint read "Blank leaves the switch's own setting alone; that is not
        // the same as off."; note 4005663823 there corrected it.
        { name: "poe", label: "PoE", type: "select", options: POE, hint: "Blank sets nothing for PoE: the port is sent with the PoE setting the controller holds for it. That is not the same as off." },
        { name: "poe_priority", label: "PoE priority", type: "select", options: PRIORITIES },
        { name: "poe_power_limit_mw", label: "PoE power limit (mW)", type: "number" },
        { name: "description", label: "Description", pattern: "^.{0,128}$" },
        { name: "enabled", label: "Enabled", type: "checkbox", hint: "Off stops the port passing traffic. The plan refuses this on the uplink." },
      ]} initial={initial} onChange={(v) => onDirty(differs(v))}
        submitLabel={intent ? `Save ${port}` : "Save planned settings"} onSubmit={(v) => void onSave(naming ? text(v.port).trim() : port, v)} onCancel={onCancel}
        problem={lane.problem} busy={lane.busy} />}
      {lane.problem?.reason === "stale" && (
        <p className="hint">
          A port&rsquo;s planned settings changed after this page read them, so nothing was saved. Your edits are kept.{" "}
          <button type="button" className="btn btn-quiet" onClick={onReload}>Read the ports again</button> and save once more.
        </p>
      )}
    </div>
  );
}
